import { Box, GridLegacy, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import { mindtimeBackupsApi } from 'src/api/mindtime/backupsApi';
import { getPageTitle } from 'src/config/projectConfig';
import BackupsChartsCard from 'src/mindtime/components/backups/BackupsChartsCard';
import BackupsetDetailsDrawer from 'src/mindtime/components/backups/BackupsetDetailsDrawer';
import type { CustomerInformation } from 'src/mindtime/components/backups/CustomerInformationCard';
import CustomerInformationCard from 'src/mindtime/components/backups/CustomerInformationCard';
import type { PackageInformation } from 'src/mindtime/components/backups/PackageInformationCard';
import PackageInformationCard from 'src/mindtime/components/backups/PackageInformationCard';
import RecentBackupActivityCard from 'src/mindtime/components/backups/RecentBackupActivityCard';
import type { SecurityStatus } from 'src/mindtime/components/backups/SecurityStatusCard';
import SecurityStatusCard from 'src/mindtime/components/backups/SecurityStatusCard';
import { expandSelectedSourceListItems } from 'src/mindtime/utils/expandMs365SelectedSourceList';
import { BackupJob, BackupsDashboardData, BackupsetDetails, BackupSetDetailsApiResponse, BackupSetItem } from 'src/types/mindtime/backups';

const MyBackups = () => {
  const location = useLocation();
  const organizationId = new URLSearchParams(location.search).get('organizationId') || undefined;
  const [dashboardData, setDashboardData] = useState<BackupsDashboardData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedBackupset, setSelectedBackupset] = useState<BackupsetDetails | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [drawerLoading, setDrawerLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [drawerError, setDrawerError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const data = await mindtimeBackupsApi.getDashboard(organizationId);
        // Validate that we received valid data structure
        if (data && typeof data === 'object') {
          setDashboardData(data);
        } else {
          console.warn('Received invalid dashboard data structure');
          // Don't break the page - components will use their default mock data
        }
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        // Don't break the page - components will use their default mock data
        setDashboardData(null);
      }
    };

    fetchDashboardData();
  }, [organizationId]);

  // Map API response to PackageInformationCard props
  const packageInfoData: PackageInformation | undefined = dashboardData?.packageInfo
    ? {
      id: dashboardData.packageInfo?.id ?? '',
      loginName: dashboardData.packageInfo?.loginName ?? '',
      loginNameIsStarred: dashboardData?.packageInfo?.status === 'ENABLE',
      package: dashboardData.packageInfo?.alias ?? `VM ${dashboardData.packageInfo?.quotaTotalGB ?? 0}GB`,
      quotaUsedGB: dashboardData.packageInfo?.quotaUsedGB ?? 0,
      quotaTotalGB: dashboardData.packageInfo?.quotaTotalGB ?? 0,
      clientHostLimit: `${dashboardData.packageInfo?.clientHostUsed ?? 0} / ${dashboardData.packageInfo?.clientHostLimit ?? 0}`,
      server: '-', // Placeholder until API provides this field
    }
    : undefined;

  // Map API response to CustomerInformationCard props
  const customerInfoData: CustomerInformation | undefined = dashboardData?.customerInfo
    ? {
      name: dashboardData.customerInfo?.name ?? '',
      company: dashboardData.customerInfo?.company ?? '',
      telephone: dashboardData.customerInfo?.phone ?? '',
      email: dashboardData.customerInfo?.email ?? '',
      createDate: dashboardData.customerInfo?.createDate ?? '',
      address: dashboardData.customerInfo?.address ?? '',
    }
    : undefined;

  // Map API response to SecurityStatusCard props
  const securityStatusData: SecurityStatus | undefined = dashboardData?.securityStatus
    ? {
      client2FAEnabled: dashboardData.securityStatus?.client2FAEnabled ?? false,
      autoUpdateEnabled: dashboardData.securityStatus?.autoUpdateEnabled ?? false,
      lastLoginDetailed: dashboardData.securityStatus?.lastLogin
        ? {
          date: dashboardData.securityStatus.lastLogin?.time ?? '',
          detail: `${dashboardData.securityStatus.lastLogin?.ipAddress ?? 'N/A'} (${dashboardData.securityStatus.lastLogin?.deviceType ?? 'Unknown'})`,
        }
        : undefined,
      lastLoginStandard: dashboardData.securityStatus?.lastPasswordChange ?? '',
      passwordChanged: dashboardData.securityStatus?.lastPasswordChange ?? '',
      settingsUpdated: dashboardData.securityStatus?.lastPasswordChange ?? '', // Placeholder until API provides dedicated field
    }
    : undefined;

  // Format a size value into GB when it's a raw numeric (bytes); otherwise return the original string.
  const formatBytesToGB = (value: number | string | undefined | null): string => {
    if (value == null) {
      return '0 GB';
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      // If the backend already sends a human-readable string with units, keep it as is.
      if (/[a-zA-Z]/.test(trimmed)) {
        return trimmed;
      }
      const parsed = Number(trimmed);
      if (Number.isNaN(parsed)) {
        return '0 GB';
      }
      const gb = parsed / (1024 * 1024 * 1024);
      return `${gb.toFixed(2)} GB`;
    }

    const gb = value / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  };

  // Format LastRestoreTime value (epoch ms or string) into a human-readable date/time.
  const formatLastRestoreTime = (value: number | string | undefined | null): string => {
    if (value == null) {
      return '-';
    }

    // Some APIs use -1 as a sentinel for \"never\".
    if (typeof value === 'number') {
      if (value <= 0) {
        return '-';
      }
      try {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
          return '-';
        }
        return (
          date.toLocaleDateString('en-GB', {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
          }) +
          ' ' +
          date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })
        );
      } catch {
        return '-';
      }
    }

    const trimmed = value.trim();
    if (!trimmed) {
      return '-';
    }

    // If the backend already returns a human-readable string, keep it.
    if (/[a-zA-Z]/.test(trimmed)) {
      return trimmed;
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return '-';
    }

    try {
      const date = new Date(parsed);
      if (Number.isNaN(date.getTime())) {
        return '-';
      }
      return (
        date.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: '2-digit',
        }) +
        ' ' +
        date.toLocaleTimeString('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        })
      );
    } catch {
      return '-';
    }
  };

  // Transform API response to BackupsetDetails format
  const transformApiResponseToDetails = (apiData: NonNullable<BackupSetDetailsApiResponse['Data']>): BackupsetDetails => {
    const primaryDestinationStats = apiData.DestinationSettings?.DestinationList?.[0]?.Statistics;
    const totalBackupFromDestination = primaryDestinationStats?.TotalBackup;
    const totalBackupFromFlat = apiData.Statistics?.TotalBackup;

    // Normalise uncompressed size so we never pass a raw object into Typography
    const rawUncompressedSize =
      apiData.data ??
      totalBackupFromDestination?.UncompressedSize ??
      totalBackupFromFlat?.UncompressedSize;

    const uncompressedSize = formatBytesToGB(rawUncompressedSize as number | string | undefined);

    const filesCount =
      totalBackupFromDestination?.FileCount ??
      totalBackupFromFlat?.FileCount;

    // Normalise deduplication saving – API may return an object with multiple size fields
    const rawDedupeSaving =
      apiData.deduplicationSaving ??
      primaryDestinationStats?.DedupeSaving;

    let dedupeSaving: string;
    if (typeof rawDedupeSaving === 'string' || typeof rawDedupeSaving === 'number') {
      dedupeSaving = String(rawDedupeSaving);
    } else if (rawDedupeSaving && typeof rawDedupeSaving === 'object') {
      const anyDedupe = rawDedupeSaving as {
        UncompressedSize?: number | string;
        CompressedSize?: number | string;
        DataSize?: number | string;
        OriginalSize?: number | string;
      };
      const sizeValue =
        anyDedupe.UncompressedSize ??
        anyDedupe.CompressedSize ??
        anyDedupe.DataSize ??
        anyDedupe.OriginalSize;
      dedupeSaving = sizeValue != null ? String(sizeValue) : '-';
    } else {
      dedupeSaving = '-';
    }

    const recycleBinEnabled = apiData.RecycleBinSettings?.Enabled;
    const recycleBin =
      typeof recycleBinEnabled === 'boolean'
        ? recycleBinEnabled
          ? 'Enabled'
          : 'Disabled'
        : apiData.recycleBin || 'Disabled';

    const lastRestoreJob =
      apiData.lastRestoreJob ||
      primaryDestinationStats?.LastRestore ||
      apiData.Statistics?.LastRestore ||
      '-';

    const lastRestoreTimeRaw =
      (primaryDestinationStats as { LastRestoreTime?: number | string } | undefined)?.LastRestoreTime ??
      (apiData.Statistics as { LastRestoreTime?: number | string } | undefined)?.LastRestoreTime;

    const lastRestoreTime = formatLastRestoreTime(lastRestoreTimeRaw ?? null);

    const lastDataIntegrityCheckRaw =
      apiData.lastDataIntegrityCheck ??
      (primaryDestinationStats as { LastDICTime?: number | string } | undefined)?.LastDICTime ??
      (apiData.Statistics as { LastDICTime?: number | string } | undefined)?.LastDICTime;

    const lastDataIntegrityCheck = formatLastRestoreTime(lastDataIntegrityCheckRaw ?? null);

    const retentionTextFromPolicy = apiData.RetentionPolicySettings
      ? `${apiData.RetentionPolicySettings.Period ?? ''} ${apiData.RetentionPolicySettings.Unit ?? ''}`.trim()
      : undefined;

    const retentionText =
      apiData.retention ||
      retentionTextFromPolicy ||
      (apiData.retentionDays != null ? `${apiData.retentionDays} Days` : '-');

    const formatScheduleFromSettings = (): string => {
      const schedule = apiData.ScheduleSettings;

      if (!schedule) {
        return apiData.scheduled || '-';
      }

      const { DailyScheduleList, WeeklyScheduleList, CustomScheduleList } = schedule;

      const formatTime = (hour?: number, minute?: number): string | null => {
        if (hour == null || minute == null) {
          return null;
        }
        const h = hour.toString().padStart(2, '0');
        const m = minute.toString().padStart(2, '0');
        return `${h}:${m}`;
      };

      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

      const dailyLines: string[] = (DailyScheduleList || [])
        .map((entry) => {
          const e = entry as { Hour?: number; Minute?: number; Name?: string };
          const timeText = formatTime(e.Hour, e.Minute);
          if (!timeText) {
            return null;
          }
          // const nameSuffix = e.Name ? ` (${e.Name})` : '';
          return `Every day at ${timeText}`;
        })
        .filter((line): line is string => Boolean(line));

      const weeklyLines: string[] = (WeeklyScheduleList || [])
        .map((entry) => {
          const e = entry as { Hour?: number; Minute?: number; Name?: string; Days?: boolean[] };
          const timeText = formatTime(e.Hour, e.Minute);
          const daysArray = Array.isArray(e.Days) ? e.Days : [];
          const activeDays = daysArray
            .map((isActive, index) => (isActive ? dayNames[index] : null))
            .filter((d): d is string => Boolean(d));

          if (!timeText || activeDays.length === 0) {
            return null;
          }

          const dayLabel = activeDays.join(', ');
          const nameSuffix = e.Name ? ` (${e.Name})` : '';
          return `Every ${dayLabel} at ${timeText}${nameSuffix}`;
        })
        .filter((line): line is string => Boolean(line));

      const customLines: string[] = (CustomScheduleList || [])
        .map((entry) => {
          const e = entry as { Date?: string; Hour?: number; Minute?: number; Name?: string };
          if (!e.Date) {
            return null;
          }
          const timeText = formatTime(e.Hour, e.Minute);
          const nameSuffix = e.Name ? ` (${e.Name})` : '';
          return timeText
            ? `On ${e.Date} at ${timeText}${nameSuffix}`
            : `On ${e.Date}${nameSuffix}`;
        })
        .filter((line): line is string => Boolean(line));

      const parts = [...dailyLines, ...weeklyLines, ...customLines];

      if (parts.length === 0) {
        return apiData.scheduled || '-';
      }

      // Separate each schedule on its own line for readability.
      return parts.join('\n');
    };

    const scheduledText = formatScheduleFromSettings();

    const backupSourceIncluded = apiData.SelectedSourceList || [];
    const backupSourceExcluded = apiData.DeselectedSourceList || apiData.FilterSettings?.FilterList || [];

    const allowedIps =
      apiData.AllowedIPList?.map((ipRange) => ({
        startIp: ipRange.StartIP ?? '',
        endIp: ipRange.EndIP ?? '',
      })).filter((range) => range.startIp || range.endIp) || [];

    const resolvedName = apiData.name || (apiData as any).Name || '';
    const resolvedId =
      apiData.backupSetID ||
      (apiData as any).ID ||
      (apiData as any).Id ||
      '';

    return {
      id: resolvedId || resolvedName,
      name: resolvedName,
      backupSetId: resolvedId,
      storageUsed: uncompressedSize || apiData.storageUsed,
      storageTotal: apiData.storageTotal || '0 kB',
      retentionDays: apiData.retentionDays ?? 14,
      statistics: {
        lastLogin: apiData.lastLogin || 'File',
        runOnComputer: apiData.pc || '-',
        scheduled: scheduledText,
        backupLoginOnline: apiData.backupLoginOnline ?? true,
        deduplicationSaving: dedupeSaving,
        data: uncompressedSize,
        files: filesCount != null ? `${filesCount}` : undefined,
        retention: retentionText,
        recycleBin,
        lastRestoreJob,
        LastRestoreTime: lastRestoreTime,
        lastDataIntegrityCheck,
        type: apiData.type,
      },
      backupJobs: apiData.backupJobs || [],
      backupSource: {
        included: expandSelectedSourceListItems(backupSourceIncluded),
        excluded: backupSourceExcluded,
      },
      settings: {
        workingDir: apiData.WorkingDir,
        dedupeEnabled: apiData.DeduplicationSettings?.Enabled,
        dedupeScope: apiData.DeduplicationSettings?.Scope,
        dedupeBlockSize: apiData.DeduplicationSettings?.BlockSize?.toString(),
        followLinks: apiData.FollowLink,
        compression: apiData.CompressType,
        restoreDrill: apiData.RestoreDrillSettings?.Enable,
        fileViewer: apiData.FileViewerEnabled,
        allowedIps,
      },
    };
  };

  // Fallback transformation when API call fails
  const transformBackupSetToDetails = (backupSet: BackupSetItem): BackupsetDetails => {
    const mockBackupJobs: BackupJob[] = [
      {
        backupStart: backupSet.lastBackupJobID || new Date().toISOString(),
        status: 'SUCCESS',
        uploaded: '1.00 MB',
        TotalNewFilesSize: '0.00 MB',
        TotalUpdatedFilesSize: '0.00 MB',
        TotalDeletedFilesSize: '0.00 MB',
      },
    ];

    return {
      id: backupSet.backupSetID || backupSet.name,
      name: backupSet.name,
      backupSetId: backupSet.backupSetID || backupSet.name,
      storageUsed: '0 kB',
      storageTotal: '0 kB',
      retentionDays: 14,
      statistics: {
        lastLogin: 'File',
        runOnComputer: backupSet.pc || '-',
        scheduled: '-',
        backupLoginOnline: true,
        deduplicationSaving: '-',
        data: '-',
        retention: '-',
        recycleBin: 'Disabled',
        lastRestoreJob: '-',
        lastDataIntegrityCheck: '-',
      },
      backupJobs: mockBackupJobs,
      backupSource: {
        included: [],
        excluded: [],
      },
      settings: {
        workingDir: undefined,
        dedupeEnabled: undefined,
        dedupeScope: undefined,
        dedupeBlockSize: undefined,
        followLinks: undefined,
        compression: undefined,
        restoreDrill: undefined,
        fileViewer: undefined,
        allowedIps: [],
      },
    };
  };

  // Handle backupset row click - calls API to get details
  const handleBackupsetClick = async (backupSet: BackupSetItem): Promise<void> => {
    const backupSetId = backupSet.backupSetID;

    if (!backupSetId) {
      console.warn('No backupSetID available for this item');
      return;
    }

    // Open drawer immediately with loading state
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerError(null);
    setSelectedBackupset(null);

    try {
      const response = await mindtimeBackupsApi.getBackupSetDetails(
        backupSetId,
        organizationId
      );

      if (response.Status === 'OK' && response.Data) {
        const details = transformApiResponseToDetails(response.Data);
        setSelectedBackupset(details);
      } else {
        // API returned error status, use fallback data
        console.warn('API returned error:', response.Message);
        const fallbackDetails = transformBackupSetToDetails(backupSet);
        setSelectedBackupset(fallbackDetails);
        setDrawerError(response.Message || 'Failed to load backupset details');
      }
    } catch (err) {
      console.error('Failed to fetch backupset details:', err);
      // Use fallback data on error
      const fallbackDetails = transformBackupSetToDetails(backupSet);
      setSelectedBackupset(fallbackDetails);
      setDrawerError('Failed to load backupset details');
    } finally {
      setDrawerLoading(false);
    }
  };

  // Handle drawer close
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedBackupset(null);
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitle('My Backups')}</title>
      </Helmet>

      <Box p={3}>
        <Typography variant="h4" style={{ fontWeight: 600 }} gutterBottom>
          My Backups
        </Typography>

        <GridLegacy container spacing={3} sx={{ mt: 0 }}>
          {/* Top row: Package Information and Customer Information side by side */}
          <GridLegacy item xs={12} md={6}>
            <PackageInformationCard data={packageInfoData} />
          </GridLegacy>
          <GridLegacy item xs={12} md={6}>
            <CustomerInformationCard data={customerInfoData} />
          </GridLegacy>

          {/* Full width: Security Status */}
          <GridLegacy item xs={12}>
            <SecurityStatusCard data={securityStatusData} />
          </GridLegacy>

          {/* Full width: Charts with tabs (Backupset quota, Backupset history, Quota history) */}
          <GridLegacy item xs={12}>
            <BackupsChartsCard
              destinations={dashboardData?.storageDistribution?.destinations}
              organizationId={organizationId}
            />
          </GridLegacy>

          {/* Full width: Recent Backup Activity */}
          <GridLegacy item xs={12}>
            <RecentBackupActivityCard
              onItemClick={handleBackupsetClick}
              organizationId={organizationId}
            />
          </GridLegacy>

          {/* Full width: Configuration & Control */}
          {/* <GridLegacy item xs={12}>
            <ConfigurationControlCard />
          </GridLegacy> */}
        </GridLegacy>
      </Box>

      {/* Drawer in the right */}
      <BackupsetDetailsDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        backupsetDetails={selectedBackupset}
        loading={drawerLoading}
        error={drawerError}
      />
    </>
  );
};

export default MyBackups;
